import { gql } from "@apollo/client";
import { Toast } from "antd-mobile";
import { useNavigate } from "react-router-dom";
import { client } from "../graphql/client";
import { DELETE_ACCOUNT_MUTATION, DeleteAccountResponse, DeleteAccountVariables, DeleteAccountInput } from "../graphql/mutations/deleteAccount";
import { LOGOUT_MUTATION, LogoutResponse, LogoutVariables, LogoutInput } from "../graphql/mutations/logout";
import {getDeviceId, isAndroid, isIOS} from "./deviceUtils";
import { setAllowNavigation } from "../hooks/usePreventEdgeSwipe";
import {notifyNativeDriverLogin, notifyNativeDriverLogout, requestNativeLogout} from "./nativeBridge";
import { t } from "../i18n";
import { DspPath } from "../enums/DeliveryNavigatePathEnum";
import { TmsPath } from "../enums/TmsNavigatePathEnum";
import {isAppVersionGreaterThanOrEqual, kDoNotPassZipCodeListVersion, kIOSNativeDeliveryVersion, kNativeLoginVersion} from "./versionUtils";
import {checkGrayscaleDriver, checkTemporaryGrayDriver} from "./grayscaleDriverUtils";
import { useBluetoothScaleStore } from '../store/bluetoothScaleStore';
import { resetAllReturnToWhsSettings } from './returnToWhsSettingsUtils';
import { resetAllScanSoundSettings } from './scanSoundSettingsUtils';

// 存储和获取认证令牌
const USER_INFO_KEY = 'swiftx_user_info';

export type AppRole =
    | 'HUB_MANAGER'
    | 'HUB_OPERATOR'
    | 'WAREHOUSE_MANAGER'
    | 'WAREHOUSE_OPERATOR'
    | 'DSP_MANAGER'
    | 'DELIVERY_DRIVER'
    | 'HEADQUARTERS_STAFF'
    | 'PICKUP_DRIVER'
    | 'PICKUP_STAFF'
    | 'SORTING_STAFF'
    | 'RETURNS_STAFF'
    | 'FINANCIAL_TEAM'
    | 'TMS_DRIVER';

export interface UserInfo {
  token: string;
  refreshToken?: string;
  account: {
    id: string;
    name: string;
    phone?: string;
    avatarUrl?: string;
    role: {
      name: AppRole;
      permissions: string[];
    };
  };
}

// 保存用户信息
export const saveUserInfo = (userInfo: UserInfo): void => {
  localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
};

// 获取用户信息
export const getUserInfo = (): UserInfo | null => {
  const userInfoStr = localStorage.getItem(USER_INFO_KEY);
  if (userInfoStr) {
    try {
      return JSON.parse(userInfoStr);
    } catch (e) {
      removeUserInfo();
      return null;
    }
  }
  return null;
};

const _isWarehouseManager = (userInfo: UserInfo | null): boolean => {
  return userInfo?.account.role.name === 'WAREHOUSE_MANAGER';
};

export const isWarehouseManager = (): boolean => {
  const userInfo = getUserInfo();
  return _isWarehouseManager(userInfo);
};

const _isHubManager = (userInfo: UserInfo | null): boolean => {
  return userInfo?.account.role.name === 'HUB_MANAGER';
};

export const isHubManager = (): boolean => {
  const userInfo = getUserInfo();
  return _isHubManager(userInfo);
};

const _isWarehouseOperator = (userInfo: UserInfo | null): boolean => {
  return userInfo?.account.role.name === 'WAREHOUSE_OPERATOR';
};

export const isWarehouseOperator = (): boolean => {
  const userInfo = getUserInfo();
  return _isWarehouseOperator(userInfo);
};

const _isHubOperator = (userInfo: UserInfo | null): boolean => {
  return userInfo?.account.role.name === 'HUB_OPERATOR';
};

export const isHubOperator = (): boolean => {
  const userInfo = getUserInfo();
  return _isHubOperator(userInfo);
};

const _isPickupStaff = (userInfo: UserInfo | null): boolean => {
  return userInfo?.account.role.name === 'PICKUP_STAFF';
};

export const isPickupStaff = (): boolean => {
  const userInfo = getUserInfo();
  return _isPickupStaff(userInfo);
};

const _isSortingStaff = (userInfo: UserInfo | null): boolean => {
  return userInfo?.account.role.name === 'SORTING_STAFF';
};

export const isSortingStaff = (): boolean => {
  const userInfo = getUserInfo();
  return _isSortingStaff(userInfo);
};

const _isReturnsStaff = (userInfo: UserInfo | null): boolean => {
  return userInfo?.account.role.name === 'RETURNS_STAFF';
};

export const isReturnsStaff = (): boolean => {
  const userInfo = getUserInfo();
  return _isReturnsStaff(userInfo);
};

export const isDspManager = (): boolean => {
  const userInfo = getUserInfo();
  return userInfo?.account.role.name === 'DSP_MANAGER';
};

export const isDeliveryDriver = (): boolean => {
  const userInfo = getUserInfo();
  return userInfo?.account.role.name === 'DELIVERY_DRIVER';
};

export const isHeadquarters = (): boolean => {
  const userInfo = getUserInfo();
  return userInfo?.account.role.name === 'HEADQUARTERS_STAFF';
};

export const isFinancialTeam = (): boolean => {
  const userInfo = getUserInfo();
  return userInfo?.account.role.name === 'FINANCIAL_TEAM';
};

// 判断是否已登录（优先 localStorage，其次 native 注入 token）
export const isLoggedIn = (): boolean => {
  const token = getToken();
  return !!token && token.length > 0;
};

export const hasPdaPermission = (userInfo: UserInfo): boolean => {
  if (
      _isWarehouseManager(userInfo) ||
      _isWarehouseOperator(userInfo) ||
      _isHubManager(userInfo) ||
      _isHubOperator(userInfo) ||
      _isPickupStaff(userInfo) ||
      _isSortingStaff(userInfo) ||
      _isReturnsStaff(userInfo)
  ) {
    return true;
  }
  return false;
}

export const hasHubOperationPermission = (): boolean => {
  return isHubManager() || isHubOperator();
}

export const hasWarehouseOperationPermission = (): boolean => {
  return isWarehouseManager() || isWarehouseOperator();
}

export const getToken = (): string | null => {
  const userInfo = getUserInfo();
  if (userInfo?.token) return userInfo.token;

  const nativeParams = (window as any).__nativeParams__ as { token?: unknown } | undefined;
  const token = nativeParams?.token;
  if (typeof token === 'string') {
    return token;
  }
  return null;
};

/**
 * 轻量级页面通用“验证后端可识别当前登录态”探针：
 * - 未登录：提示并直接返回
 * - 已登录：展示 loading toast，执行 request，成功/失败提示
 *
 * 注意：这是 UI 级工具，返回值可用于页面后续逻辑（如展示 count）。
 */
export const probeLoggedInBackendWithToast = async <T>(params: {
  isLoggedIn: () => boolean;
  notLoggedInText: string;
  loadingText: string;
  errorText: string;
  request: () => Promise<T>;
  getSuccessText: (result: T) => string;
}): Promise<T | null> => {
  if (!params.isLoggedIn()) {
    Toast.show(params.notLoggedInText);
    return null;
  }

  Toast.show({
    icon: 'loading',
    content: params.loadingText,
    duration: 0,
  });

  try {
    const result = await params.request();
    Toast.clear();
    Toast.show(params.getSuccessText(result));
    return result;
  } catch {
    Toast.clear();
    Toast.show(params.errorText);
    return null;
  }
};

export const getRefreshToken = (): string | null => {
  const userInfo = getUserInfo();
  return userInfo?.refreshToken ?? null;
};

/**
 * 获取最终的 deviceId
 * 如果提供了 deviceId，直接使用；否则尝试从设备获取
 * @param deviceId 可选的设备ID
 * @returns 最终的设备ID，获取失败时返回 null
 */
const getFinalDeviceId = (deviceId?: string | null): string | null => {
  if (deviceId !== undefined) {
    return deviceId;
  }
  try {
    return getDeviceId();
  } catch (error) {
    // 获取 deviceId 失败，使用 null（不影响登录流程）
    return null;
  }
};

// 删除用户信息
export const removeUserInfo = (): void => {
  localStorage.removeItem(USER_INFO_KEY);
};

// 登出
export const logout = async (
    redirectTo: string = '/login',
    source: 'web' | 'native' = 'web'
): Promise<void> => {
  // >= 8.8.0：Web 发起的退出统一交给 native 处理（DSP、揽收、TMS 等角色的退出按钮在 web 侧）
  if (source === 'web' && (isIOS() || isAndroid())) {
    const supportsNativeLogin = await isAppVersionGreaterThanOrEqual(kNativeLoginVersion);
    if (supportsNativeLogin) {
      requestNativeLogout();
      return;
    }
  }

  // 调用后端登出接口（记录登出日志 + 清理设备会话）
  try {
    const deviceId = getDeviceId();
    const input: LogoutInput = {
      deviceId: deviceId ?? null,
    };
    await client.mutate<LogoutResponse, LogoutVariables>({
      mutation: LOGOUT_MUTATION,
      variables: { input }
    });
  } catch (error) {
    console.error('[Auth] 登出API调用异常:', error);
  }

  // 清理蓝牙连接
  try {
    await useBluetoothScaleStore.getState().cleanup();
    console.log('[Auth] 蓝牙已清理');
  } catch (error) {
    console.error('[Auth] 清理蓝牙时出错:', error);
  }

  // 清理返仓设置
  try {
    resetAllReturnToWhsSettings();
    console.log('[Auth] 返仓设置已清理');
  } catch (error) {
    console.error('[Auth] 清理返仓设置时出错:', error);
  }

  // 清理扫码提示音设置
  try {
    resetAllScanSoundSettings();
    console.log('[Auth] 提示音设置已清理');
  } catch (error) {
    console.error('[Auth] 清理提示音设置时出错:', error);
  }

  // 设置允许导航标记，绕过页面离开确认
  setAllowNavigation(true);


  // 在删除用户信息之前，先获取司机ID并通知native端
  const userInfo = getUserInfo();
  if (userInfo) {
    removeUserInfo();
    notifyNativeDriverLogout(userInfo.account.id);
  }

  // 根据来源决定跳转方式
  if (source === 'native') {
    // iOS主动调用：发送事件给DeliveryLayout处理（客户端路由，断网也能工作）
    console.log('[Auth] iOS调用登出，发送事件通知Layout');
    window.dispatchEvent(new CustomEvent('app:logout', {
      detail: { redirectTo }
    }));
  } else {
    // Web主动调用：使用原有的window.location.href
    window.location.href = redirectTo;
  }
};

/**
 * 删除账号（调用API将账号状态设置为0）
 * @param redirectTo 删除后重定向的路径
 * @returns 是否删除成功
 */
export const deactivateAccount = async (redirectTo: string = '/login'): Promise<boolean> => {
  try {
    console.log('开始删除账号...');

    // 检查用户是否已登录
    const userInfo = getUserInfo();
    if (!userInfo) {
      console.error('用户未登录，无法删除账号');
      return false;
    }

    console.log('用户信息:', userInfo);

    // 获取设备ID，如果获取失败则使用null
    let deviceId: string | null = null;
    try {
      deviceId = getDeviceId();
      console.log('设备ID:', deviceId);
    } catch (error) {
      console.warn('获取设备ID失败，将使用null:', error);
      deviceId = null;
    }

    const input: DeleteAccountInput = {
      deviceId: deviceId
    };

    const { data, errors } = await client.mutate<DeleteAccountResponse, DeleteAccountVariables>({
      mutation: DELETE_ACCOUNT_MUTATION,
      variables: { input }
    });

    console.log('API响应:', { data, errors });

    if (errors) {
      console.error('DeleteAccount API error:', errors);
      return false;
    }

    if (data?.deleteAccount.success) {
      console.log('删除成功，清除本地存储并重定向');

      // 清理返仓设置
      try {
        const { resetAllReturnToWhsSettings } = await import('./returnToWhsSettingsUtils');
        resetAllReturnToWhsSettings();
        console.log('[Auth] 返仓设置已清理');
      } catch (error) {
        console.error('[Auth] 清理返仓设置时出错:', error);
      }

      // 清理扫码提示音设置
      try {
        resetAllScanSoundSettings();
        console.log('[Auth] 提示音设置已清理');
      } catch (error) {
        console.error('[Auth] 清理提示音设置时出错:', error);
      }

      // 设置允许导航标记，绕过页面离开确认
      setAllowNavigation(true);
      // 通知native端登出
      notifyNativeDriverLogout(userInfo.account.id);
      // 清除本地存储的用户信息
      removeUserInfo();
      // 重定向到登录页面
      window.location.href = redirectTo;
      return true;
    } else {
      console.error('DeleteAccount failed:', data?.deleteAccount.message);
      return false;
    }
  } catch (error) {
    console.error('DeleteAccount error:', error);
    return false;
  }
};

interface LoginResponse {
  login?: {
    success: boolean;
    token?: string | null;
    refreshToken?: string | null;
    account?: UserInfo['account'] | null;
    message?: string | null;
  };
  loginWithVerificationCode?: {
    success: boolean;
    token?: string | null;
    refreshToken?: string | null;
    account?: UserInfo['account'] | null;
    message?: string | null;
  };
}

interface LoginInput {
  countryCode: number;
  phoneOrEmail: string;
  password: string;
  deviceId?: string | null;
  platform?: string | null;
  versionName?: string | null;
}

interface LoginVariables {
  input: LoginInput;
}

interface LoginWithVerificationCodeInput {
  countryCode: number;
  phone: string;
  verificationCode: string;
  deviceId?: string | null;
  removeBlacklist?: boolean;
  platform?: string | null;
  versionName?: string | null;
}

interface LoginWithVerificationCodeVariables {
  input: LoginWithVerificationCodeInput;
}

// 账号密码登录
const LOGIN_MUTATION = gql`
  mutation LoginMutation($input: LoginInput!) {
    login(input: $input) {
      success
      token
      refreshToken
      account {
        id
        name
        phone
        avatarUrl
        role {
          name
          permissions
        }
      }
      message
    }
  }`;

// 验证码登录
const LOGIN_WITH_VERIFICATION_CODE_MUTATION = gql`
  mutation LoginWithVerificationCodeMutation($input: LoginWithVerificationCodeInput!) {
    loginWithVerificationCode(input: $input) {
      success
      token
      refreshToken
      account {
        id
        name
        phone
        avatarUrl
        role {
          name
          permissions
        }
      }
      message
    }
  }`;

// 发送验证码
const SEND_VERIFICATION_CODE_MUTATION = gql`
  mutation SendVerificationCode($input: SendVerificationCodeInput!) {
    sendVerificationCode(input: $input) {
      success
      messageKey
    }
  }
`;

const VERIFY_VERIFICATION_CODE_MUTATION = gql`
  mutation VerifyVerificationCode($input: VerifyVerificationCodeInput!) {
    verifyVerificationCode(input: $input) {
      success
      messageKey
    }
  }
`;

interface SendVerificationCodeResponse {
  sendVerificationCode: {
    success: boolean;
    messageKey?: string | null;  // 国际化消息键，用于前端进行国际化和错误类型判断
  };
}

interface VerifyVerificationCodeResponse {
  verifyVerificationCode: {
    success: boolean;
    messageKey?: string | null;
  };
}

/**
 * 登录
 * @param countryCode
 * @param phoneOrEmail 手机号或邮箱
 * @param password 密码
 * @param deviceId 设备ID（可选，派送司机必需）
 * @returns 用户信息
 * @throws 登录失败异常
 */
export const login = async (
    countryCode: number,
    phoneOrEmail: string,
    password: string,
    deviceId?: string | null,
    platform?: string | null,
    versionName?: string | null
): Promise<{ userInfo: UserInfo | null; messageKey?: string | null }> => {
  const finalDeviceId = getFinalDeviceId(deviceId);

  const { data, errors } = await client.mutate<LoginResponse, LoginVariables>({
    mutation: LOGIN_MUTATION,
    variables: {
      input: {
        countryCode,
        phoneOrEmail,
        password,
        deviceId: finalDeviceId,
        platform,
        versionName,
      },
    }
  });

  if (errors) {
    throw errors[0];
  }

  const result = data?.login;
  if (result?.success && result.token && result.account) {
    return {
      userInfo: {
        token: result.token,
        refreshToken: result.refreshToken ?? undefined,
        account: result.account
      },
      messageKey: null
    };
  } else {
    return {
      userInfo: null,
      messageKey: result?.message || null
    };
  }
};

/**
 * 验证码登录
 * @param countryCode 国家代码
 * @param phone 手机号
 * @param verificationCode 验证码
 * @param deviceId 设备ID（可选，派送司机必需）
 * @param removeBlacklist 是否需要移出黑名单（可选，默认false）
 * @returns 用户信息
 * @throws 登录失败异常
 */
export const loginWithVerificationCode = async (
    countryCode: number,
    phone: string,
    verificationCode: string,
    deviceId?: string | null,
    removeBlacklist?: boolean,
    platform?: string | null,
    versionName?: string | null
): Promise<{ userInfo: UserInfo | null; messageKey?: string | null }> => {
  const finalDeviceId = getFinalDeviceId(deviceId);

  const { data, errors } = await client.mutate<LoginResponse, LoginWithVerificationCodeVariables>({
    mutation: LOGIN_WITH_VERIFICATION_CODE_MUTATION,
    variables: {
      input: {
        countryCode,
        phone,
        verificationCode,
        deviceId: finalDeviceId,
        removeBlacklist: removeBlacklist ?? false,
        platform,
        versionName,
      },
    }
  });

  if (errors) {
    throw errors[0];
  }

  const result = data?.loginWithVerificationCode;
  if (result?.success && result.token && result.account) {
    return {
      userInfo: {
        token: result.token,
        refreshToken: result.refreshToken ?? undefined,
        account: result.account
      },
      messageKey: null
    };
  } else {
    return {
      userInfo: null,
      messageKey: result?.message || null
    };
  }
};

/**
 * 发送验证码
 * @param countryCode 国家代码
 * @param phone 手机号
 * @param type 验证码类型（默认：login）
 * @returns 发送结果，包含 success 和 messageKey
 * @throws 发送失败异常
 */
export const sendVerificationCode = async (
    countryCode: number,
    phone: string,
    type: string = "login"
): Promise<{ success: boolean; messageKey?: string | null }> => {
  const { data, errors } = await client.mutate<SendVerificationCodeResponse>({
    mutation: SEND_VERIFICATION_CODE_MUTATION,
    variables: {
      input: {
        countryCode,
        phone,
        type,
      },
    },
  });

  if (errors) {
    throw errors[0];
  }

  const result = data?.sendVerificationCode;
  return {
    success: result?.success ?? false,
    messageKey: result?.messageKey
  };
};

/**
 * 验证验证码
 */
export const verifyVerificationCode = async (
    countryCode: number,
    phone: string,
    code: string,
    type: string = "login"
): Promise<{ success: boolean; messageKey?: string | null }> => {
  const { data, errors } = await client.mutate<VerifyVerificationCodeResponse>({
    mutation: VERIFY_VERIFICATION_CODE_MUTATION,
    variables: {
      input: {
        countryCode,
        phone,
        code,
        type,
      },
    },
  });

  if (errors) {
    throw errors[0];
  }

  const result = data?.verifyVerificationCode;
  return {
    success: result?.success ?? false,
    messageKey: result?.messageKey,
  };
};

/**
 * 处理登录成功后的导航（Hook）
 * 返回一个函数，用于处理登录成功后的导航逻辑
 * @returns 处理登录成功的函数
 */
export const useHandleLoginSuccess = () => {
  const navigate = useNavigate();

  return async (userInfo: UserInfo): Promise<void> => {
    switch (userInfo.account.role.name) {
      case 'DELIVERY_DRIVER':
        saveUserInfo(userInfo);
        const supportsDeliveryNative = await isAppVersionGreaterThanOrEqual(kIOSNativeDeliveryVersion);
        const androidDeliveryNative = await isAppVersionGreaterThanOrEqual(kDoNotPassZipCodeListVersion);
        // 判断是否为灰度司机
        void checkTemporaryGrayDriver();
        // 检查灰度司机配置
        checkGrayscaleDriver(isIOS() ? 'delivery.native_app.driver_ids' : 'delivery.native_app.android.driver_ids').then(async (isGrayscale) => {
          // iOS 8.5.0/Android 8.7.0开始，派送登录成功后
          if ((supportsDeliveryNative && isIOS() && isGrayscale) || (androidDeliveryNative && isAndroid() && isGrayscale)) {
            notifyNativeDriverLogin(userInfo);
            navigate('/');
          } else {
            // <8.5.0 || android || 非灰度用户
            Toast.show(t('login.success'));
            setTimeout(() => {
              navigate('/');
            }, 2000);
          }
        });
        break;
      case 'DSP_MANAGER':
        saveUserInfo(userInfo);
        Toast.show(t('login.success'));
        setTimeout(() => {
          navigate(DspPath.DELIVERY_DSP_HOME);
        }, 2000);
        break;
      case 'PICKUP_DRIVER':
        saveUserInfo(userInfo);
        Toast.show(t('login.success'));
        setTimeout(() => {
          navigate("/pickup/picking-up");
        }, 2000);
        break;
      case 'TMS_DRIVER':
        saveUserInfo(userInfo);
        Toast.show(t('login.success'));
        setTimeout(() => {
          navigate(TmsPath.TMS_TRANSPORTING);
        }, 2000);
        break;
      default:
        Toast.show(t('errors.permission.currentAccountRoleAccessDenied'));
        break;
    }
  };
};